if (!customElements.get('card-color-variant')) {
class CardColorVariant extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.onItemClick.bind(this));
    this.list = this.querySelector('.card-color-variant__list');
    this.setupOverflowHints();
    this.setupScrollHint();
  }

  // El botón "+N" (pill con flecha en desktop, badge en mobile — ver
  // component-card-color-variant.css) avanza el carrusel un "page" en vez de
  // requerir touch/drag/scroll horizontal; solo aparece cuando is-overflow-end
  // (queda contenido a la derecha). Su par --prev retrocede igual, solo en
  // desktop (is-overflow-start, hay contenido a la izquierda ya scrolleado).
  setupScrollHint() {
    if (!this.list) return;
    const next = this.querySelector('[data-scroll-hint-next]');
    const prev = this.querySelector('[data-scroll-hint-prev]');
    if (next) {
      next.addEventListener('click', () => {
        this.list.scrollBy({ left: this.list.clientWidth, behavior: 'smooth' });
      });
    }
    if (prev) {
      prev.addEventListener('click', () => {
        this.list.scrollBy({ left: -this.list.clientWidth, behavior: 'smooth' });
      });
    }
  }

  // En mobile la fila de swatches es un carrusel (ver
  // component-card-color-variant.css). Estas clases dibujan el degradado en el
  // borde por el que todavía queda contenido, para que se entienda que hay más
  // colores de los que se ven.
  setupOverflowHints() {
    if (!this.list) return;
    const update = this.updateOverflowHints.bind(this);
    this.list.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
    window.addEventListener('load', update, { once: true });
    update();
  }

  updateOverflowHints() {
    if (!this.list) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.list;
    const hasOverflow = scrollWidth > clientWidth + 2;
    const isOverflowStart = hasOverflow && scrollLeft > 2;
    const isOverflowEnd = hasOverflow && scrollLeft + clientWidth < scrollWidth - 2;
    this.list.classList.toggle('is-overflow-start', isOverflowStart);
    this.list.classList.toggle('is-overflow-end', isOverflowEnd);
    // Also on the host: .card-color-variant__scroll-hint (the "keep scrolling"
    // arrow) is a sibling of .card-color-variant__list, not a descendant, so it
    // needs the state on a shared ancestor to be styled from CSS.
    this.classList.toggle('is-overflow-start', isOverflowStart);
    this.classList.toggle('is-overflow-end', isOverflowEnd);
  }

  getCatalog() {
    const el = this.querySelector('[data-color-variant-catalog]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  onItemClick(event) {
    const item = event.target.closest('.card-color-variant__item');
    if (!item || item.classList.contains('is-active')) return;

    event.preventDefault();
    event.stopPropagation();

    const catalog = this.getCatalog();
    const product = catalog.find((entry) => String(entry.id) === String(item.dataset.productId));
    if (!product) return;

    this.querySelectorAll('.card-color-variant__item').forEach((chip) => {
      const active = chip === item;
      chip.classList.toggle('is-active', active);
      if (active) {
        chip.setAttribute('aria-current', 'true');
      } else {
        chip.removeAttribute('aria-current');
      }
    });

    this.applyProduct(product);

    // event.detail is 0 for keyboard-triggered clicks (Enter/Space) and >0 for
    // real mouse clicks. Only release focus on a mouse click, so the quick-add
    // buttons go back to being hover-only instead of staying visible via
    // :focus-within; keyboard users keep focus so they can still reach them.
    if (event.detail !== 0) item.blur();
  }

  applyProduct(product) {
    const cardWrapper = this.closest('.card-wrapper, .cart-recs__item');
    if (!cardWrapper) return;

    const image = cardWrapper.querySelector('[data-color-variant-image]');
    if (image && product.image?.src) {
      image.src = product.image.src;
      image.alt = product.image.alt || '';
      image.removeAttribute('srcset');
      if (product.image.width) {
        image.width = product.image.width;
        image.height = product.image.height;
      }
    }

    let secondaryImage = cardWrapper.querySelector('[data-color-variant-image-secondary]');
    if (this.dataset.showSecondaryImage === 'true' && product.secondaryImage?.src) {
      if (!secondaryImage && image) {
        secondaryImage = image.cloneNode(false);
        secondaryImage.removeAttribute('data-color-variant-image');
        secondaryImage.removeAttribute('srcset');
        secondaryImage.removeAttribute('loading');
        secondaryImage.setAttribute('data-color-variant-image-secondary', '');
        image.insertAdjacentElement('afterend', secondaryImage);
      }
      if (secondaryImage) {
        secondaryImage.src = product.secondaryImage.src;
        secondaryImage.alt = product.secondaryImage.alt || '';
        secondaryImage.removeAttribute('srcset');
      }
    } else if (secondaryImage) {
      // .media--hover-effect decides which image to fade in/out purely from
      // :only-child/:first-child in CSS, so a hidden-but-present <img> still
      // breaks the hover effect on the primary image. Must remove it, not hide it.
      secondaryImage.remove();
    }

    cardWrapper.querySelectorAll('[data-color-variant-link]').forEach((link) => {
      link.href = product.url;
    });

    cardWrapper.querySelectorAll('[data-color-variant-title]').forEach((el) => {
      el.textContent = product.title;
    });

    const priceWrap = cardWrapper.querySelector('[data-color-variant-price]');
    if (priceWrap && product.priceHtml !== undefined) priceWrap.innerHTML = product.priceHtml;

    const badgeWrap = cardWrapper.querySelector('[data-color-variant-badge]');
    if (badgeWrap && product.badgeHtml !== undefined) badgeWrap.innerHTML = product.badgeHtml;

    cardWrapper.querySelectorAll('[data-color-variant-quickview]').forEach((quickViewButton) => {
      quickViewButton.setAttribute('data-product-url', product.url);
    });

    const variantInput = cardWrapper.querySelector('[data-color-variant-variant-input]');
    if (variantInput && product.variantId) {
      variantInput.value = product.variantId;
      variantInput.disabled = !product.variantAvailable;
    }

    const addButton = cardWrapper.querySelector('[data-color-variant-add-button]');
    if (addButton) {
      addButton.disabled = !product.variantAvailable;
      addButton.hidden = Boolean(product.showVariantPanel);
      // Cart drawer recommendations add to cart via AJAX using the variant id
      // read straight off this button (no hidden form input like card-product).
      if (addButton.dataset.variantId !== undefined && product.variantId) {
        addButton.dataset.variantId = product.variantId;
      }
    }

    const addText = cardWrapper.querySelector('[data-color-variant-add-text]');
    if (addText) addText.textContent = this.dataset[product.variantAvailable ? 'labelAdd' : 'labelSoldOut'];

    // The toggle lives in .card__variant-slot, separate from the panel it
    // controls (.card-variant-panel-wrapper, over the title) — keep its
    // hidden state in sync with the panel's when a color swap changes
    // whether the new product has size options.
    const panelToggle = cardWrapper.querySelector('[data-panel-toggle]');
    if (panelToggle) panelToggle.hidden = !product.showVariantPanel;

    const variantPanelWrapper = cardWrapper.querySelector('.card-variant-panel-wrapper');
    const variantPanel = cardWrapper.querySelector('[data-variant-panel]');
    if (variantPanelWrapper && variantPanel) {
      variantPanelWrapper.hidden = !product.showVariantPanel;
      if (product.showVariantPanel && Array.isArray(product.variants)) {
        // data-cart-rec-variant/the spinner are no-ops on a card-product panel
        // (nothing there listens for them) but are required for cart drawer
        // recommendations, which reuse this same panel markup and rebuild it here.
        variantPanel.innerHTML = product.variants
          .map((variant) => {
            const classes = variant.available
              ? 'card-variant-panel__option'
              : 'card-variant-panel__option is-unavailable';
            const disabledAttr = variant.available ? '' : 'disabled';
            const label = document.createElement('div');
            label.textContent = variant.label || '';
            return `<button type="button" class="${classes}" data-variant-option data-cart-rec-variant data-variant-id="${variant.id}" ${disabledAttr}><span data-variant-option-text>${label.innerHTML}</span><div class="loading__spinner hidden"><svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg></div></button>`;
          })
          .join('');
      }
      // The chip count/labels just changed — re-measure whether the prev/next
      // scroll arrows are needed (card-variant-panel.js owns that logic).
      if (typeof variantPanelWrapper.updateArrows === 'function') {
        variantPanelWrapper.updateArrows();
      }
    }
  }
}

customElements.define('card-color-variant', CardColorVariant);
}
